"use client";

import { Observable } from "@legendapp/state";
import { useObservable } from "@legendapp/state/react";
import { useMount } from "@legendapp/state/react";
import { PropsWithChildren } from "react";

import { setCookie } from "@/app/_actions/setCookie";

import { createCustomContext } from "./context";
import { COOKIES, COOKIES_TIMES } from ".constants/cookies";
import { FEATURE_FLAGS } from ".constants/featureFlags";

export type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const [Context, useThemeContext] = createCustomContext<
  Observable<ThemeContextType>
>({
  name: "ThemeContext",
});
export { useThemeContext };

/** тёмная тема */
const DARK_THEME = "dark";

/** светлая тема (если включено в `FEATURE_FLAGS`) */
const LIGHT_THEME = FEATURE_FLAGS.IS_LIGHT_THEME_ENABLED ? "light" : "dark";

interface ProviderProps extends PropsWithChildren {
  theme: Theme;
}

export function ThemeProvider({ children, theme: $theme }: ProviderProps) {
  const theme$ = useObservable<ThemeContextType>({
    /** стейт темы с initial из серверных куков (props) */
    theme: $theme,
    /** Функция-туглер темы
     * - опрелеляем "новую" тему
     * - прописываем атрибут `data-theme` для `html`
     * - записываем куки текущей темы
     */
    toggleTheme: () => {
      const root = window.document.documentElement;
      const newTheme =
        theme$.theme.get() === DARK_THEME ? LIGHT_THEME : DARK_THEME;
      root.setAttribute("data-theme", newTheme);
      theme$.theme.set(newTheme);
      setCookie(COOKIES.NEXT_THEME, newTheme, {
        maxAge: COOKIES_TIMES.YEAR_1,
      });
    },
  });

  //** При маунте проверяем тему и прописываем нужные атрибуты */
  useMount(() => {
    const root = window.document.documentElement;

    let newTheme: Theme = "system";

    /**
     * - если "автоопределение темы" по системым настройкам включено во FEATURE_FLAGS
     * - и текущий стейт темы - "system" (пришел с пропсами при ренедеринге),
     * - то мы проверяем настройки браузера и выставляем тему согласно им.
     * - иначе выставляем тему согласно стейту (с проверкой FEATURE_FLAGS разрешена ли светала тема)
     */
    if (
      FEATURE_FLAGS.IS_DETECT_THEME_ENABLED &&
      theme$.theme.get() === "system"
    ) {
      newTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? DARK_THEME
        : LIGHT_THEME;
    } else {
      newTheme = FEATURE_FLAGS.IS_LIGHT_THEME_ENABLED
        ? theme$.theme.get()
        : DARK_THEME;
    }

    root.setAttribute("data-theme", newTheme);
    theme$.theme.set(newTheme);
    setCookie(COOKIES.NEXT_THEME, newTheme, {
      maxAge: COOKIES_TIMES.YEAR_1,
    });
  });

  return <Context value={theme$}>{children}</Context>;
}
